import React, { useEffect, useState } from "react";

const CustomTableAccordionContext = React.createContext(null);

/**
 * @name CustomTableAccordion
 * @function
 * @param {STRING} className - Additional class(s) to add onto the accordion
 * @param {STRING} dataPoint - Data point that should be added to the accordion
 * @extends React.Component
 * @exported
 * @description The main component for a custom table accordion
 * @example
 * export default function Component(props){
 *  return (
 *      <CustomTableAccordion className="someClass">
 *          ...
 *      </CustomTableAccordion>
 *  );
 * }
 */
export default function CustomTableAccordion(props) {
    const active = props.activeClass ?? "";
    const className = props.className ?? "";
    const point = props.dataPoint ?? "";

    const [ state, setState ] = useState(props.state ?? false);
    const controller = {
        "state" : state,
        "toggle" : () => setState(!state),
        "close" : () => setState(false),
        "open" : () => setState(true)
    };

    useEffect(() => {
        setState(props.state);
    }, [props.state]);

    return (
        <CustomTableAccordionContext.Provider value={ controller }>
            <tbody className={ `custom-transaction-accordion ${ state ? active : "" } ${ className }` } data-point={ point }>
                { props.children }
            </tbody>
        </CustomTableAccordionContext.Provider>
    );
}

export const useCustomTableAccordion = () => React.useContext(CustomTableAccordionContext);

/**
 * @name CustomTableToggle
 * @function
 * @extends React.Component
 * @exported true
 * @description Wrapper to toggle the accordion content
 * @notes Clones the child element binding an onclick event
 * @example
 * export default function Component(props){
 *  return (
 *      <CustomTableAccordion className="someClass">
 *          <tr>
 *              <CustomTableToggle>
 *                  <td>Open</td>
 *              </CustomTableToggle>
 *          </tr>
 *      </CustomTableAccordion>
 *  );
 * }
 */
export function CustomTableToggle(props) {
    const controller = useCustomTableAccordion();
    const onClick = () => {
        controller.toggle();

        if(props.onClick instanceof Function) {
            props.onClick(!controller.state);
        }
    }

    if(!controller) {
        throw new Error('Unable to access "useCustomTableAccordion" hook. CustomTableToggle must be a child of CustomTableAccordion');
    }
    let child = props.children instanceof Array ? props.children[0]:props.children;  
    return React.cloneElement(child, { onClick });
}

/**
 * @name CustomTableContent
 * @function
 * @extends React.Component
 * @description Wrapper for the custom table content
 * @notes Returns the child elements based on current state
 * @example
 * export default function Component(props){
 *  return (
 *      <CustomTableAccordion className="someClass">
 *          <tr>
 *              <CustomTableToggle>
 *                  <td>Open</td>
 *              </CustomTableToggle>
 *          </tr>
 * 
 *          <CustomTableContent>
 *              <tr>
 *                  ...
 *              </tr>
 *          </CustomTableContent>
 *      </CustomTableAccordion>
 *  );
 * }
 */
export function CustomTableContent(props) {
    const controller = useCustomTableAccordion();

    if(!controller) {
        throw new Error('Unable to access "useCustomTableAccordion" hook. CustomTableContent must be a child of CustomTableAccordion');
    }

    return controller.state && props.children;
}